/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { AfterViewInit, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';
import { WINDOW } from 'src/app/core/core.module';
import { IdentityService } from 'src/app/core/identity.service';
import { MarginCalculatorModel } from 'src/app/margin-calculator/models/margin-calculator.model';
import { MarginCalculatorInsuranceService } from 'src/app/margin-calculator/services/margin-calculator-insurance.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ContractRequestService } from 'src/app/shared/lib/contract-request/contract-request.service';
import { MarginCalculatorFormService } from '../../services/margin-calculator-form.service';
import {
    MarginCalculatorPageActions,
    PipelineMarginActions,
    ReviewAndSendActions,
    UpdateMarginActions
} from '../../store/actions';
import * as Selectors from '../../store/selectors';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

export enum MarginTypes {
    NewMarginCalculator = 1,
    QuickQuote = 2,
    OldMarginCalculator = 3
}

@UntilDestroy()
@Component({
    selector: 'nova-margin-calculator-button-pane-ngrx',
    templateUrl: './margin-calculator-button-pane.component.html',
    styleUrls: ['./margin-calculator-button-pane.component.scss'],
    standalone: true,
    imports: [MatButtonModule, MatTooltipModule, NgIf, NgClass, AsyncPipe]
})
export class MarginCalculatorButtonPaneComponent implements OnInit, AfterViewInit {
    @Output()
    marginSaved = new EventEmitter<number>();

    @Output()
    contractIdReceived: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    marginDuplicated = new EventEmitter();

    isRecruiting$ = this.store.select(Selectors.selectIsRecruiting);
    isNurseContracting$ = this.store.select(Selectors.selectIsNurseContracting);
    margin: MarginCalculatorModel = null;
    contractId = 0;
    isPipeLineEnabled = false;
    isSaveEnabled$ = this.store.select(Selectors.selectIsMarginSaveEnabled);
    userId = 0;
    marginId = 0;
    formDisabled$ = this.store.select(Selectors.selectMarginFormDisabled);
    success = 0;
    failure = -1;
    success$ = of(this.success);
    failure$ = of(this.failure);
    marginExists$ = this.store.select(Selectors.selectMarginExists);
    isPipelined$ = this.store.select(Selectors.selectIsPipelined);
    reviewAndSendButtonDisabled$ = this.store.select(Selectors.selectReviewAndSendButtonDisabled);
    reviewSendTooltip$ = this.store.select(Selectors.selectReviewAndSendTooltip);
    saveTooltip$ = this.store.select(Selectors.selectSaveTooltip);

    constructor(
        private readonly _marginFormService: MarginCalculatorFormService,
        private readonly _marginInsuranceService: MarginCalculatorInsuranceService,
        private readonly _router: Router,
        private readonly _identityService: IdentityService,
        private readonly contractRequestService: ContractRequestService,
        private readonly store: Store,
        @Inject(WINDOW) private readonly _window: Window
    ) {}

    ngOnInit(): void {
        this._setMarginValues();

        this.store
            .select(Selectors.selectUserId)
            .pipe(untilDestroyed(this))
            .subscribe((userId) => (this.userId = userId));

        this._setupSaveMarginConfirmedPostProcess();
        this._setupDuplicateMarginPostProcess();
        this._setupPipelineMarginPostProcess();
        this._setupReviewAndSendMarginPostProcess();
    }

    ngAfterViewInit(): void {
        this.store
            .select(Selectors.selectMarginFormDisabled)
            .pipe(untilDestroyed(this))
            .subscribe((disabled) => {
                if (disabled) {
                    this._marginFormService.setFormReadonly();
                } else {
                    this._marginFormService.setFormEditable();
                }
            });
    }

    openPayEmailDetails(): void {
        this.store.dispatch(MarginCalculatorPageActions.revealPayPackageDetails());
    }

    saveMargin(): void {
        this.store.dispatch(UpdateMarginActions.saveClicked());
    }

    contractRequest(): void {
        this.store.dispatch(ReviewAndSendActions.reviewAndSendClicked());
    }

    contractMod(): void {
        this.store.dispatch(MarginCalculatorPageActions.contractMod());
    }

    duplicateMargin(): void {
        this.store.dispatch(MarginCalculatorPageActions.duplicateMargin());
    }

    pipelineMargin(): void {
        this.store.dispatch(PipelineMarginActions.pipelineMargin());
    }

    private _setupPipelineMarginPostProcess(): void {
        this.store
            .select(Selectors.selectSaveMarginActionStatusSuccess)
            .pipe(
                withLatestFrom(this.store.select(Selectors.selectSaveMarginActionTypePipeline)),
                filter(([isSuccess, isActionType]) => isSuccess && isActionType),
                withLatestFrom(this.store.select(Selectors.selectContractId)),
                untilDestroyed(this)
            )
            .subscribe(([, contractId]) => {
                this.contractId = contractId;
                this.isPipeLineEnabled = true;
            });
    }

    private _setupDuplicateMarginPostProcess(): void {
        this.store
            .select(Selectors.selectSaveMarginActionStatusSuccess)
            .pipe(
                withLatestFrom(this.store.select(Selectors.selectSaveMarginActionTypeDuplicate)),
                filter(([isSuccess, isActionType]) => isSuccess && isActionType),
                withLatestFrom(this.store.select(Selectors.selectMarginId)),
                untilDestroyed(this)
            )
            .subscribe(([, marginId]) => {
                if (marginId) {
                    this.marginId = marginId;
                    this._marginInsuranceService.endDateInitiallyLoaded = false;
                    this._marginInsuranceService.clinicianInitiallyLoaded = false;
                    this.marginDuplicated.emit();
                    this._router.navigateByUrl(
                        `/margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`
                    );
                }
            });
    }

    private _setupSaveMarginConfirmedPostProcess(): void {
        this.store
            .select(Selectors.selectSaveMarginActionStatusSuccess)
            .pipe(
                withLatestFrom(this.store.select(Selectors.selectSaveMarginActionTypeSaveMargin)),
                filter(([isSuccess, isActionType]) => isSuccess && isActionType),
                withLatestFrom(this.store.select(Selectors.selectMarginId)),
                untilDestroyed(this)
            )
            .subscribe({
                next: ([, marginId]) => {
                    if (marginId) {
                        this.isPipeLineEnabled = true;
                        this.marginSaved.emit(marginId);
                        this._openSnackBar(marginId);
                    } else {
                        this.isPipeLineEnabled = false;
                    }
                },
                error: (err) => {
                    this._showToasterMessage('Margin failed to save!');
                    console.error('something wrong occurred: ' + err);
                }
            });
    }

    private _setupReviewAndSendMarginPostProcess(): void {
        this.store
            .select(Selectors.selectSaveMarginActionStatusSuccess)
            .pipe(
                withLatestFrom(this.store.select(Selectors.selectSaveMarginActionTypeReviewAndSend)),
                filter(([isSuccess, isActionType]) => isSuccess && isActionType),
                withLatestFrom(
                    this.store.select(Selectors.selectContractId),
                    this.store.select(Selectors.selectContractRequestId),
                    this.store.select(Selectors.selectMarginId)
                ),
                untilDestroyed(this)
            )
            .subscribe(([, , contractRequestId, marginId]) => {
                if (contractRequestId > 0) {
                    this._showToasterMessage(
                        'Saving Contract Request, please be patient.  New Page will open when done'
                    );
                    this._window.open(
                        `//${this._window.location.host}/#/recruiting/contract-request/${contractRequestId}?marginId=${marginId}`,
                        `_blank`
                    );
                    this._window.close();
                }
            });
    }

    private _openSnackBar(marginId: number): void {
        this.marginId = marginId;
        this._showToasterMessage(`Margin saved: ${marginId}`);
    }

    private _setContractValue(contractId: number): void {
        if (contractId > 0) {
            this.contractIdReceived.emit(contractId);
            this.isPipeLineEnabled = true;
        } else {
            this.isPipeLineEnabled = false;
        }
    }

    private _setMarginValues(): void {
        this.store
            .select(Selectors.selectContractId)
            .pipe(
                filter((contractId) => !isNaN(contractId)),
                withLatestFrom(this.store.select(Selectors.selectMarginAsModel)),
                untilDestroyed(this)
            )
            .subscribe(([, margin]) => {
                this.margin = margin;
                this.marginId = this.margin.id;
                this._setContractValue(this.margin.contractId);
            });
    }

    private _showToasterMessage(message: string): void {
        this.store.dispatch(
            showToasterMessage({
                message,
                duration: 2000
            })
        );
    }
}
